import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Plus, Search, Briefcase, Bookmark, BookmarkCheck, Send } from "lucide-react";

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

function timeAgo(iso) {
    if (!iso) return "";
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
}

// Detail view for one Job Role — its searches plus the shortlisted / contacted / saved
// candidates aggregated across all of the job's searches. (The :projectId route param
// carries the GenieHire job_id.)
const ProjectDetail = () => {
    const navigate = useNavigate();
    const { projectId: jobId } = useParams();
    const orgId = localStorage.getItem("current_org_id");

    const [job, setJob] = useState(null);
    const [searches, setSearches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchJob = useCallback(() => {
        setLoading(true);
        axios
            .get(`${SOURCING_URL}/sourcing/job-roles/${jobId}`, { params: { org_id: orgId } })
            .then(({ data }) => { setJob(data); setSearches(data.searches || []); })
            .catch(() => setError("Failed to load job role."))
            .finally(() => setLoading(false));
    }, [jobId, orgId]);

    useEffect(() => { fetchJob(); }, [fetchJob]);

    const jobRole = job?.job_role || "Job role";

    const openResults = (s) =>
        navigate("/org-admin/sourcing/results", {
            state: { query: s.alias || s.raw_query, search_id: s.search_id, job_id: jobId, job_role: jobRole },
        });

    const newSearch = () =>
        navigate("/org-admin/sourcing", { state: { job_id: jobId, job_role: jobRole } });

    const openTab = (path) =>
        navigate(path, { state: { job_id: jobId, searchName: jobRole } });

    return (
        <div className="min-h-screen bg-bgSecondary dark:bg-none flex">
            {/* ── Left sidebar ─────────────────────────────────────────────── */}
            <aside className="w-64 shrink-0 bg-bgPrimary dark:glass-effect border-r border-gray-200 dark:border-gray-700/50 flex flex-col px-3 py-4 gap-1">
                <div className="flex items-center gap-2 px-3 py-2 mb-1">
                    <span className="text-sm font-semibold text-textPrimary dark:text-[#D9D9D9] truncate" title={jobRole}>{jobRole}</span>
                </div>

                <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700/50 flex flex-col gap-1">
                    <button
                        onClick={() => openTab("/org-admin/sourcing/shortlist")}
                        className="flex items-center justify-between gap-2 w-full px-3 py-2 rounded-lg text-sm text-textSecondary dark:text-[#CCCCCCE5] hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                        <span className="flex items-center gap-2"><Bookmark className="w-4 h-4" /> Shortlist</span>
                        <span className="text-xs text-purple-600 dark:text-purple-300 bg-purple-50 dark:bg-purple-500/10 rounded-full px-2 py-0.5">{job?.shortlist_count ?? 0}</span>
                    </button>
                    <button
                        onClick={() => openTab("/org-admin/sourcing/saved")}
                        className="flex items-center justify-between gap-2 w-full px-3 py-2 rounded-lg text-sm text-textSecondary dark:text-[#CCCCCCE5] hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                        <span className="flex items-center gap-2"><BookmarkCheck className="w-4 h-4" /> Saved</span>
                        <span className="text-xs text-gray-500 dark:text-gray-300 bg-gray-100 dark:bg-gray-700/40 rounded-full px-2 py-0.5">{job?.saved_count ?? 0}</span>
                    </button>
                    <button
                        onClick={() => openTab("/org-admin/sourcing/contacted")}
                        className="flex items-center justify-between gap-2 w-full px-3 py-2 rounded-lg text-sm text-textSecondary dark:text-[#CCCCCCE5] hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                        <span className="flex items-center gap-2"><Send className="w-4 h-4" /> Contacted</span>
                        <span className="text-xs text-gray-500 dark:text-gray-300 bg-gray-100 dark:bg-gray-700/40 rounded-full px-2 py-0.5">{job?.contacted_count ?? 0}</span>
                    </button>
                </div>
            </aside>

            {/* ── Main panel ───────────────────────────────────────────────── */}
            <div className="flex-1 min-w-0">
                <div className="bg-bgPrimary dark:glass-effect border-b border-gray-200 dark:border-gray-700/50 px-6 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2 text-sm min-w-0">
                        <button onClick={() => navigate("/org-admin/sourcing/projects")} className="text-textSecondary hover:text-textPrimary truncate">
                            Job Roles
                        </button>
                        <span className="text-gray-400 dark:text-gray-500">›</span>
                        <span className="text-textPrimary dark:text-[#D9D9D9] font-medium truncate">{jobRole}</span>
                    </div>
                    <button
                        onClick={newSearch}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium text-white shrink-0 hover:opacity-90"
                        style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                    >
                        <Plus className="w-4 h-4" /> New search
                    </button>
                </div>

                <div className="px-6 py-6 max-w-4xl">
                    {loading ? (
                        <div className="flex justify-center py-24">
                            <div className="w-7 h-7 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : error ? (
                        <p className="text-sm text-red-500 dark:text-red-400 text-center py-24">{error}</p>
                    ) : searches.length === 0 ? (
                        <div className="flex flex-col items-center text-center py-24">
                            <Search className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-4" />
                            <p className="text-base font-semibold text-textPrimary dark:text-[#D9D9D9]">No searches yet</p>
                            <p className="text-sm text-textSecondary mt-1 mb-6">Run a search to add candidates to this job role.</p>
                            <button
                                onClick={newSearch}
                                className="flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-medium text-white hover:opacity-90"
                                style={{ background: "linear-gradient(to right, #ec4899, #3b82f6)" }}
                            >
                                <Plus className="w-4 h-4" /> New search
                            </button>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {searches.map(s => (
                                <div
                                    key={s.search_id}
                                    className="bg-bgPrimary dark:glass-effect rounded-xl border border-gray-200 dark:border-gray-700/50 px-4 py-3 hover:shadow-sm hover:border-purple-200 dark:hover:border-purple-500/40 transition-all flex items-center gap-3"
                                >
                                    <button onClick={() => openResults(s)} className="flex items-center gap-3 min-w-0 flex-1 text-left">
                                        <Search className="w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0" />
                                        <span className="text-sm text-textSecondary dark:text-[#CCCCCCE5] truncate">{s.alias || s.raw_query}</span>
                                    </button>
                                    <div className="flex items-center gap-3 shrink-0">
                                        <span className="text-xs text-gray-400 dark:text-gray-500">{s.result_count} candidates</span>
                                        <span className="text-xs text-gray-400 dark:text-gray-500">{timeAgo(s.created_at)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProjectDetail;
